import { z } from "zod";
import { clinicalPhaseSchema } from "./clinical-phase";
import type { Locale } from "./common";
import { treatmentCycleLabelSchema } from "./treatment-surface";

// TES-1295 (TES-1267 S8, §5, §6, §10; TES-1299) — the read models behind
// app 2.0.9's Din vård (TES-1298): one structured "Din behandling" object,
// a chronological "Vårdhistorik" index that links to originals, and the
// newest doctor's note with its read state.
//
// 09C "GRUNDMODELL — EN KÄLLA, FLERA VISNINGAR": nothing here is a second copy.
// Din behandling is rendered from the structured ordination on the
// prescription cycle (and, once TES-1292 lands, the medication catalogue)
// plus the published decision. The history carries type, label, date,
// doctor and a target id, never the text of what it points at
// ("Vårdhistorik länkar till original utan textkopior").
//
// Purely additive: three new GETs (patient-api/v1/din-vard/…). Builds up to
// 2.0.8 never call them, so no contract date gates them.

// ---- Din behandling ---------------------------------------------------------

// TES-1299 — before the verified subscription releases the prescription, the
// approved decision is shown in this mode only. Emelie's label, verbatim
// (09C "KANONISK SYNKNING 2026-10-13").
export const AWAITING_ACTIVATION_STATUS_LABEL =
  "Behandling godkänd – inväntar aktivering";

// TES-1612 — the English of every server-built label in this file, served
// when the patient's language is English (patient.preferredLocale, which the
// app keeps in step with its own language: TES-638, TES-876). Until then an
// English app showed these labels in Swedish. Where the app's en.json already
// translates the same Swedish words, that wording is used and its key named;
// the rest is new English, listed in the PR as copy proposals for Emelie.
export const AWAITING_ACTIVATION_STATUS_LABEL_BY_LOCALE: Record<
  Locale,
  string
> = {
  sv: AWAITING_ACTIVATION_STATUS_LABEL,
  // en.json healthCare.status.decisionAwaitingActivation.
  en: "Treatment approved – awaiting activation",
};

// After the release. Wording of ours (TES-1267 §4 names the states
// "behandlingsuppehåll" and "avslutad behandling"); listed as invented copy.
export const TREATMENT_STATUSES = ["active", "paused", "ended"] as const;
export const treatmentStatusSchema = z.enum(TREATMENT_STATUSES);
export type TreatmentStatus = z.infer<typeof treatmentStatusSchema>;
export const TREATMENT_STATUS_LABEL: Record<TreatmentStatus, string> = {
  active: "Pågående behandling",
  paused: "Behandlingsuppehåll",
  ended: "Avslutad behandling",
};
// TES-1612 — all three English labels are new (a copy proposal for Emelie).
export const TREATMENT_STATUS_LABEL_BY_LOCALE: Record<
  Locale,
  Record<TreatmentStatus, string>
> = {
  sv: TREATMENT_STATUS_LABEL,
  en: {
    active: "Ongoing treatment",
    paused: "Treatment paused",
    ended: "Treatment ended",
  },
};

export const publishedDecisionSchema = z.object({
  outcome: z.enum(["approved", "not_approved"]),
  // When the doctor published it (CP-09 notice, or the decided transfer
  // review). Null only for a row that carries no time, never guessed.
  decidedAt: z.string().nullable(),
});
export type PublishedDecision = z.infer<typeof publishedDecisionSchema>;

// The prescription as it concerns her. `confirmedAt` is when a named doctor
// confirmed the prescription in Webdoc (`prescribedAt`); null when this
// version was activated without a new one ("Befintligt recept gäller").
export const PRESCRIPTION_STATUSES = [
  "new_issued",
  "existing_valid",
  "unknown",
] as const;
export const patientPrescriptionSchema = z.object({
  status: z.enum(PRESCRIPTION_STATUSES),
  confirmedAt: z.string().nullable(),
  // Only with existing_valid, when the doctor knows it (YYYY-MM-DD).
  existingValidUntil: z.string().nullable(),
});
export type PatientPrescription = z.infer<typeof patientPrescriptionSchema>;

// TES-1292 (S5) — the catalogue product of this ordination. Null until the
// catalogue is wired in, and for every free-text ordination after it.
export const treatmentProductSchema = z.object({
  name: z.string(),
  activeSubstance: z.string().nullable(),
  penVariant: z.string().nullable(),
});
export type TreatmentProduct = z.infer<typeof treatmentProductSchema>;

// "Rätt läkemedel öppnar rätt officiell instruktion; andra läkemedels
// instruktioner visas inte." Only ever her product's and pen's guide, from
// the catalogue's current version, and only while the link is active.
export const officialGuideSchema = z.object({
  url: z.string(),
  filmUrl: z.string().nullable(),
  sender: z.string().nullable(),
  label: z.string(),
});
export type OfficialGuide = z.infer<typeof officialGuideSchema>;

// "Läkarens kommentar till behandlingsbeslutet" of this version. Stored
// once; shown here and reachable from the history by its notice id.
export const treatmentCommentSchema = z.object({
  noticeId: z.string(),
  text: z.string(),
  publishedAt: z.string(),
  publishedBy: z.string().nullable(),
});
export type TreatmentComment = z.infer<typeof treatmentCommentSchema>;

// One version of the treatment object: the first decision's ordination, a
// renewal, a dose change. Every value comes from a structured column.
export const treatmentVersionSchema = z.object({
  cycleNumber: z.number().int(),
  // current | superseded | previous, as on the treatment surface.
  label: treatmentCycleLabelSchema,
  // The ordination as written in its structured fields (TES-636); all
  // null when the row predates them, never parsed out of a label.
  medication: z.string().nullable(),
  strength: z.string().nullable(),
  interval: z.string().nullable(),
  plannedLength: z.string().nullable(),
  product: treatmentProductSchema.nullable(),
  officialGuide: officialGuideSchema.nullable(),
  prescription: patientPrescriptionSchema,
  // When this version became hers (the release, or a renewal's start).
  activatedAt: z.string().nullable(),
  comment: treatmentCommentSchema.nullable(),
});
export type TreatmentVersion = z.infer<typeof treatmentVersionSchema>;

export const treatmentStartSchema = z.object({
  on: z.string(),
  // first_dose — her own registration of the first dose (Ö-137);
  // transfer_verified — the doctor's verified start of a transfer (TES-734).
  source: z.enum(["first_dose", "transfer_verified"]),
  // Only for transfer_verified: exact | estimated.
  precision: z.string().nullable(),
});

const awaitingActivationSchema = z.object({
  mode: z.literal("awaiting_activation"),
  statusLabel: z.string(),
  decision: publishedDecisionSchema,
  activation: z.object({
    // Her verified address, masked ("m•••@gmail.com"); null when she has no
    // verified address. The app draws "Skicka informationen igen" and
    // "Kontakta Ella" itself.
    maskedEmail: z.string().nullable(),
  }),
  // TES-1471 (Ö-494) — "Publicerat beslut och läkarens kommentar är läsbara
  // före aktivering": the comment published with the approval, read from
  // that same notice (its id, never a copy kept elsewhere). Null when the
  // approval had no text. Optional so an older API still parses; builds up
  // to 2.0.11 ignore it.
  comment: treatmentCommentSchema.nullable().optional(),
});

const notApprovedSchema = z.object({
  mode: z.literal("not_approved"),
  decision: publishedDecisionSchema,
});

const activeSchema = z.object({
  mode: z.literal("active"),
  status: treatmentStatusSchema,
  statusLabel: z.string(),
  // Null for a treatment released before decisions were published as
  // notices: the release itself is the fact, and no date is invented.
  decision: publishedDecisionSchema.nullable(),
  clinicalPhase: clinicalPhaseSchema.nullable(),
  // The version in force now; null once the treatment has ended and no
  // version is live (the versions stay reachable through the history).
  version: treatmentVersionSchema.nullable(),
  startedOn: treatmentStartSchema.nullable(),
  // System-computed (cycle/dose-day.ts, the same rule as Hem's card); null
  // when no date can be computed safely.
  nextDoseOn: z.string().nullable(),
  latestMedicalUpdateAt: z.string().nullable(),
  // TES-1472 (TES-1458 §3) — "Efter aktivering och faktiskt utfärdat recept:
  // Ditt recept är klart. Nu kan du hämta ut ditt läkemedel." True only while
  // the version in force carries a prescription a named doctor issued in
  // Webdoc, its explicit pickup value is true and she has not reported the
  // pickup. A transfer on an existing prescription, a planned or unregistered
  // prescription and a feedback-only month are all false. The prescription
  // status itself stays internal ("Receptstatus används internt, inte som en
  // separat teknisk patientruta"). Optional so an older API still parses;
  // builds up to 2.0.11 ignore it.
  pickupDue: z.boolean().optional(),
});

export const currentTreatmentSchema = z.discriminatedUnion("mode", [
  awaitingActivationSchema,
  notApprovedSchema,
  activeSchema,
]);
export type CurrentTreatment = z.infer<typeof currentTreatmentSchema>;

// GET patient-api/v1/din-vard/treatment. `treatment` is null before any
// decision is published.
export const currentTreatmentResponseSchema = z.object({
  treatment: currentTreatmentSchema.nullable(),
});
export type CurrentTreatmentResponse = z.infer<
  typeof currentTreatmentResponseSchema
>;

// GET patient-api/v1/din-vard/treatment/versions/:cycleNumber — where a
// treatment event in the history opens. 404 for a version that is not hers
// or not released to her.
export const treatmentVersionResponseSchema = z.object({
  version: treatmentVersionSchema,
});
export type TreatmentVersionResponse = z.infer<
  typeof treatmentVersionResponseSchema
>;

// ---- Care history -----------------------------------------------------------

// Where an entry opens. Ids only: the app fetches the original.
//   note       → GET patient-api/v1/medical-notices/:noticeId
//   treatment  → Din behandling; `cycleNumber` names the version
//                (…/din-vard/treatment/versions/:cycleNumber), null = the
//                current object (a decision before the release)
//   lab_order  → that sampling under "Prover och mätningar"
export const careHistoryTargetSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("note"), noticeId: z.string() }),
  z.object({
    kind: z.literal("treatment"),
    cycleNumber: z.number().int().nullable(),
  }),
  z.object({ kind: z.literal("lab_order"), labOrderId: z.string() }),
]);
export type CareHistoryTarget = z.infer<typeof careHistoryTargetSchema>;

export const CARE_HISTORY_EVENT_TYPES = [
  // A text the doctor wrote for her (doctor_notice, medical_question_answer).
  "note",
  // The published treatment decision (CP-09, or the transfer review).
  "decision",
  // The published monthly review: an event label, not a free-text type.
  "monthly_review",
  // A new version whose ordination differs from the one before.
  "plan_changed",
  // A new version with the same ordination.
  "renewal",
  "pause",
  "end",
  // A blood sample ordered, and the doctor's published word on its result.
  "lab_order",
  "lab_feedback",
] as const;
export const careHistoryEventTypeSchema = z.enum(CARE_HISTORY_EVENT_TYPES);
export type CareHistoryEventType = z.infer<typeof careHistoryEventTypeSchema>;

// Labels. "Medicinsk månadsåterkoppling", "Ändrad behandlingsplan" and
// "Läkarens besked om provsvar" are TES-1267's words; "Receptförnyelse",
// "Behandlingsuppehåll" and "Avslutad behandling" its §4 nouns;
// "Behandlingsbeslut" the app's existing heading. "Besked från din läkare"
// and "Provtagning" are ours (invented copy, listed in the PR).
export const CARE_HISTORY_LABEL: Record<CareHistoryEventType, string> = {
  note: "Besked från din läkare",
  decision: "Behandlingsbeslut",
  monthly_review: "Medicinsk månadsåterkoppling",
  plan_changed: "Ändrad behandlingsplan",
  renewal: "Receptförnyelse",
  pause: "Behandlingsuppehåll",
  end: "Avslutad behandling",
  lab_order: "Provtagning",
  lab_feedback: "Läkarens besked om provsvar",
};
// TES-1612 — the English labels. From en.json: note
// (dinVard.latestNote.title), decision (healthCare.entries.decisions.title),
// renewal (home.dinaSteg.renewal.title), lab_order (home.tasks.labs.title),
// lab_feedback (dinVard.labs.feedbackNotice). monthly_review builds on the
// app's "monthly review" for "månadsåterkoppling" (monthlyReview.title);
// it, plan_changed, pause and end are new English (copy proposals for
// Emelie).
export const CARE_HISTORY_LABEL_BY_LOCALE: Record<
  Locale,
  Record<CareHistoryEventType, string>
> = {
  sv: CARE_HISTORY_LABEL,
  en: {
    note: "Message from your doctor",
    decision: "Treatment decision",
    monthly_review: "Medical monthly review",
    plan_changed: "Changed treatment plan",
    renewal: "Prescription renewal",
    pause: "Treatment paused",
    end: "Treatment ended",
    lab_order: "Blood test",
    lab_feedback: "Your doctor's word on your lab results",
  },
};

export const careHistoryEntrySchema = z.object({
  type: careHistoryEventTypeSchema,
  label: z.string(),
  date: z.string(),
  // The responsible doctor's name, or null when the event has none
  // (a system transition) or the account is gone.
  doctor: z.string().nullable(),
  target: careHistoryTargetSchema,
});
export type CareHistoryEntry = z.infer<typeof careHistoryEntrySchema>;

// "Senaste beskedet från din läkare": the newest published note, with "Nytt"
// until she has opened it (MedicalNoticeRead, TES-1136). Not a copy: the
// app opens the note by id, and opening it is what marks it read
// (POST …/medical-notices/:id/read). The history lists every OTHER note:
// "Nyare besked flyttar föregående besked till historik utan
// dataduplicering", so a newer note moves this one there and nothing is
// listed twice.
export const latestNoteSchema = z.object({
  noticeId: z.string(),
  publishedAt: z.string(),
  publishedBy: z.string().nullable(),
  read: z.boolean(),
});
export type LatestNote = z.infer<typeof latestNoteSchema>;

// GET patient-api/v1/din-vard/history — newest first.
export const careHistoryResponseSchema = z.object({
  latestNote: latestNoteSchema.nullable(),
  entries: z.array(careHistoryEntrySchema),
});
export type CareHistoryResponse = z.infer<typeof careHistoryResponseSchema>;
